import { ProjectFileTypeEnum, ProjectStatus, RateType } from '@cyberlabrs/helpers';

export enum EmploymentType {
    FULL_TIME = 'Full-time',
    PART_TIME = 'Part-time',
    CONTRACT = 'Contract',
}

export enum ActivityType {
    NOTE = 'note',
    MEET = 'meet',
}

export interface ProjectDto {
    id: string;
    clientId: string;
    organizationId: string;
    name: string;
    description?: string;
    active: boolean;
    archivedAt?: string | null;
    archivedByUserSub?: string | null;
    isArchived?: boolean;
    createdAt: Date;
    updatedAt: Date;
}

export interface CreateProjectDto {
    name: string;
    clientId: string | number;
    active: boolean;
    description?: string;
    service?: string;
    projectStatus?: ProjectStatus;
    startDate?: string;
    endDate?: string;
    assignees?: AssigneeDto[];
}

export type ProjectRoleDto = {
    id: number;
    name: string;
};

type ProjectAssigneeRoleDto = {
    userSub: string;
    projectId: string;
    projectRoleDto: ProjectRoleDto;
    createdAt: string;
    updatedAt: string;
};

export type ProjectAssigneeListItemDto = {
    userSub: string;
    projectId: string;
    active: boolean;
};

export type ProjectAssigneeListItemWithUserInfoDto = ProjectAssigneeListItemDto & {
    email: string;
    givenName?: string;
    familyName?: string;
    imageUrl?: string;
    title?: string;
    branchName?: string;
};

export type ProjectAssigneeDto = {
    userSub: string;
    projectId: string;
    active: boolean;
    assignedRoles: ProjectAssigneeRoleDto[];
    createdAt: string;
    updatedAt: string;
    markedAsObsolete: boolean;
    rate?: number;
    rateType?: RateType;
    billable?: boolean;
    currencyCode?: string;
    startDate: string;
};

export type ProjectDtoV2 = {
    id: string;
    clientId: string;
    organizationId: string;
    name: string;
    active: boolean;
    service?: string;
    description?: string;
    archivedAt?: string | null;
    archivedByUserSub?: string | null;
    isArchived?: boolean;
    createdAt: Date;
    updatedAt: Date;
};

export type ProjectDtoRelV2 = ProjectDtoV2 & {
    projectAssignees: ProjectAssigneeListItemWithUserInfoDto[];
    clientName?: string;
    startDate?: string;
    endDate?: string;
    projectStatus?: ProjectStatus;
};

type AssignedRole = {
    projectRoleId: number;
    name?: string;
};

export type AssigneeDto = {
    userSub: string | undefined;
    assignedRoles: AssignedRole[];
    billable?: boolean;
    rate?: number;
    rateType?: RateType;
    employmentType?: string;
    startDate?: string;
};

export type AddAssigneesDto = {
    projectId: string;
    assignees: AssigneeDto[];
};

export type UpdateAssigneeDto = {
    rate: number;
    rateType: RateType;
    billable: boolean;
    employmentType: EmploymentType;
    rateEffectiveFrom?: string;
    currencyEffectiveFrom?: string;
    compensationIntervalStart?: string;
    compensationIntervalEnd?: string;
    currencyCode?: string;
    startDate?: string;
};

export type ProjectAssigneeRateRowDto = {
    id: string;
    userSub: string;
    projectId: string;
    rate: number | null;
    rateType?: RateType;
    startDate?: string;
    endDate?: string | null;
    effectiveFrom: string;
    effectiveTo: string | null;
};

export type ProjectAssigneeCurrencyRowDto = {
    id: string;
    userSub: string;
    projectId: string;
    currencyCode: string;
    startDate?: string;
    endDate?: string | null;
    effectiveFrom: string;
    effectiveTo: string | null;
};

export type ProjectAssigneeAssignmentPeriodRowDto = {
    id: string;
    userSub: string;
    projectId: string;
    startDate?: string;
    endDate?: string | null;
    effectiveFrom: string;
    effectiveTo: string | null;
};

export type UserProjectDto = {
    id: string;
    name: string;
    userSub: string;
    description: string;
    active: boolean;
    createdAt: string;
};

export type UserProjectWithClientInfoDto = {
    id: string;
    name: string;
    userSub: string;
    description: string;
    active: boolean;
    createdAt: Date;
    clientId: string;
    clientName: string;
    projectStatus?: ProjectStatus;
};

export interface UpdateProjectDto {
    name?: string;
    clientId?: string;
    description?: string;
    active?: boolean;
    service?: string;
    startDate?: string;
    endDate?: string;
    assignees?: AssigneeDto[];
}

export type ProjectAssigneesWithUserInfoDto = ProjectAssigneeListItemDto & {
    email: string;
    givenName?: string;
    familyName?: string;
    imageUrl?: string;
    title?: string;
    branchName?: string;
    assignedRoles: ProjectAssigneeRoleDto[];
    createdAt: string;
    updatedAt: string;
    markedAsObsolete: boolean;
    rate?: number;
    rateType?: RateType;
    billable?: boolean;
    employmentType?: string;
    currencyCode?: string;
    startDate: string;
    rateEffectiveFrom?: string;
    currencyEffectiveFrom?: string;
    compensationIntervalStart?: string;
    compensationIntervalEnd?: string | null;
};

export type OrganizationProjectsCount = {
    countProjects: number;
    createdAtCount: number;
};

export type ProjectPublicDto = {
    id: string;
    name: string;
    clientId: string;
};

export interface MeetActivity {
    projectId?: string;
    clientId: string;
    title: string;
    description: string;
    date: string;
    time?: string;
    link?: string;
    createdBy: string;
}

export interface NoteActivity {
    projectId?: string;
    clientId: string;
    title: string;
    description: string;
    createdBy: string;
}

export interface ActivityCreator {
    userSub: string;
    givenName?: string;
    familyName?: string;
    imageUrl?: string;
}

export interface NoteActivityDto {
    id: string;
    projectId: string;
    title: string;
    description: string;
    createdBy: string;
    createdAt: string;
    updatedAt: string;
}

export interface MeetActivityDto {
    id: string;
    projectId: string;
    title: string;
    description: string;
    date: string;
    time?: string;
    link?: string;
    createdBy: string;
    createdAt: string;
    updatedAt: string;
}

export interface ActivityFeedDto {
    noteActivities: NoteActivityDto[];
    meetActivities: MeetActivityDto[];
}

export interface NoteActivityWithUserDto extends NoteActivityDto {
    createdByName: string;
    createdByImageUrl?: string;
    projectName?: string;
}

export interface MeetActivityWithUserDto extends MeetActivityDto {
    createdByName: string;
    createdByImageUrl?: string;
    projectName?: string;
}

export interface ActivityFeedWithUserDto {
    noteActivities: NoteActivityWithUserDto[];
    meetActivities: MeetActivityWithUserDto[];
}

export type ProjectWithUserInfoDto = ProjectDto & {
    givenName: string;
    familyName: string;
    imageUrl?: string;
    clientName: string;
};

export type ProjectFileDto = {
    id: string;
    projectId: string;
    name: string;
    s3Name: string;
    date: string;
    time: string;
    fileType: ProjectFileTypeEnum;
    markedAsObsolete: boolean;
    createdAt: Date;
    updatedAt: Date;
};

export type ProjectFileWithPresignedUrlDto = ProjectFileDto & {
    presignedUrl: string;
};

export type ProjectFilePostDto = {
    projectId: string;
    file: File;
    fileType: ProjectFileTypeEnum;
    name: string;
    onSuccess?: () => void;
};
